import React, { useContext, useEffect, useState } from "react";
import CurrencyList from "currency-list";
import { Formik } from "formik";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import Loader from "../../../../components/spinners/Loader";
import NotyfContext from "../../../../contexts/NotyfContext";
import { useWhitelabel } from "../../../../contexts/WhitelabelContext";
import useWindowSize from "../../../../hooks/useWindowSize";
import { getPortfolioInvoices } from "../../../../redux/slices/invoices";
import { Form, Modal, Offcanvas } from "react-bootstrap";
import RegisterPaymentForm from "./RegisterPaymentForm";
import EditEntityForm from "../EditEntityForm";
import { registerPayment } from "../../../../redux/slices/payments";
import useDeviceType from "../../../../hooks/useDeviceType";

const RegisterPaymentModal = ({
  showRegisterPaymentModal,
  setShowRegisterPaymentModal,
  client,
}) => {
  const currencyList = CurrencyList.getAll("en_US");
  const { backgroundColor, fontColor } = useWhitelabel();
  const [isLoading, setIsLoading] = useState(true);
  const dispatch = useDispatch();
  const notyf = useContext(NotyfContext);
  const { t } = useTranslation();
  const [width] = useWindowSize();
  const deviceType = useDeviceType();
  const isDesktop = deviceType === "desktop";
  const [internalShow, setInternalShow] = useState(showRegisterPaymentModal);
  const [fadeOut, setFadeOut] = useState(false);
  const invoices = useSelector((state) => state.invoices.invoices);

  useEffect(() => {
    if (showRegisterPaymentModal && !isDesktop) {
      setFadeOut(false);
      setInternalShow(true);
    }
  }, [showRegisterPaymentModal]);

  const handleHide = () => {
    setFadeOut(true);
    setTimeout(() => {
      setInternalShow(false);
      setShowRegisterPaymentModal(false);
    }, 300);
  };

  useEffect(() => {
    const fetchInvoices = async () => {
      setIsLoading(true);
      await dispatch(getPortfolioInvoices({ id: client.id }))
        .then(() => {
          setIsLoading(false);
        })
        .catch((error) => {
          notyf.open({
            type: "danger",
            message: "Unable to retrieve invoices",
            duration: 5000,
            ripple: true,
            dismissible: false,
            position: {
              x: "center",
              y: "top",
            },
          });
          setShowRegisterPaymentModal(false);
        });
    };

    if (client.id && showRegisterPaymentModal) {
      fetchInvoices();
    }
  }, [client.id, showRegisterPaymentModal]);

  const initialValues = {
    amount: "",
    currency: "",
    invoiceId: "",
  };

  const validation = Yup.object().shape({
    amount: Yup.number()
      .min(0, "Must be a positive number")
      .required("Required"),
    currency: Yup.string().required("Required"),
    invoiceId: Yup.string().required("Required"),
  });

  if (isDesktop) {
    return (
      <Modal
        show={showRegisterPaymentModal}
        onHide={() => setShowRegisterPaymentModal(false)}
        centered
        scrollable
        backdrop="static"
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("crm.registerPayment.title")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Formik
            initialValues={initialValues}
            validationSchema={validation}
            onSubmit={async (values, { setValues }) => {
              const data = {
                amount: values.amount,
                currency: values.currency,
                invoiceId: values.invoiceId,
              };

              await dispatch(registerPayment({ data }))
                .then(() => {
                  notyf.open({
                    type: "success",
                    message: "Payment successfully registered",
                    duration: 5000,
                    ripple: true,
                    dismissible: false,
                    position: {
                      x: "center",
                      y: "top",
                    },
                  });
                })
                .catch((error) => {
                  notyf.open({
                    type: "danger",
                    message: error,
                    duration: 5000,
                    ripple: true,
                    dismissible: false,
                    position: {
                      x: "center",
                      y: "top",
                    },
                  });
                })
                .finally(() => {
                  setShowRegisterPaymentModal(false);
                  setValues(initialValues);
                });
            }}
          >
            {({
              errors,
              handleBlur,
              handleSubmit,
              handleChange,
              touched,
              values,
              setValues,
              setFieldValue,
              setFieldTouched,
            }) => {
              return (
                <RegisterPaymentForm
                  values={values}
                  errors={errors}
                  touched={touched}
                  setFieldValue={setFieldValue}
                  setFieldTouched={setFieldTouched}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  setValues={setValues}
                  setShowRegisterPaymentModal={setShowRegisterPaymentModal}
                  initialValues={initialValues}
                  handleSubmit={handleSubmit}
                  invoices={invoices}
                  currencyList={currencyList}
                />
              );
            }}
          </Formik>
        </Modal.Body>
      </Modal>
    );
  } else {
    return (
      <Offcanvas
        show={internalShow}
        placement="bottom"
        backdrop="static"
        scrollable={true}
        className={`fullscreen-offcanvas ${fadeOut ? "fade-out" : ""}`}
        onHide={handleHide}
      >
        <Offcanvas.Header closeButton className="fullscreen-offcanvas-header">
          <Offcanvas.Title>{t("crm.registerPayment.title")}</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="fullscreen-offcanvas-body">
          <Formik
            initialValues={initialValues}
            validationSchema={validation}
            onSubmit={async (values, { setValues }) => {
              const data = {
                amount: values.amount,
                currency: values.currency,
                invoiceId: values.invoiceId,
              };

              await dispatch(registerPayment({ data }))
                .then(() => {
                  notyf.open({
                    type: "success",
                    message: "Payment successfully registered",
                    duration: 5000,
                    ripple: true,
                    dismissible: false,
                    position: {
                      x: "center",
                      y: "top",
                    },
                  });
                })
                .catch((error) => {
                  notyf.open({
                    type: "danger",
                    message: error,
                    duration: 5000,
                    ripple: true,
                    dismissible: false,
                    position: {
                      x: "center",
                      y: "top",
                    },
                  });
                })
                .finally(() => {
                  setShowRegisterPaymentModal(false);
                  setValues(initialValues);
                });
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              setFieldValue,
              setFieldTouched,
              handleSubmit,
              setValues,
            }) => {
              return (
                <RegisterPaymentForm
                  values={values}
                  errors={errors}
                  touched={touched}
                  setFieldValue={setFieldValue}
                  setFieldTouched={setFieldTouched}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  setValues={setValues}
                  handleSubmit={handleSubmit}
                  invoices={invoices}
                  currencyList={currencyList}
                />
              );
            }}
          </Formik>
        </Offcanvas.Body>
      </Offcanvas>
    );
  }
};

export default RegisterPaymentModal;
